'use client';

import React, { StrictMode, Suspense, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { matchRoute, studentRoutes } from '@mildang/design-system/type-routes';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LinguiClientProvider } from '@shared/utils/LinguiClientProvider';
import ThemeRegistry from '@ui/utils/ThemeRegistry';
import Toaster from '@ui/components/molecules/Toaster';
import GlobalConfirm from '@mildang/design-system-legacy/esm/components/molecules/dialog/ConfirmDialog/extends/confirm/GlobalConfirm';
import { RouterSystemProvider } from '@mildang/design-system-legacy/esm/context/routerSystem';
import NewFeedListener from '@ui/components/feed/patterns/NewFeedListener';
import useCheckClientVersion from '@ui/hooks/useCheckClientVersion';
import pkg from '../../../package.json';
import { activityManager } from '@ui/utils/ActivityManager';
import isSSR from 'utils/isSSR';
import { useHostStore } from '@ui/store/host';
import { initMildangFirebaseApp } from '@ui/utils/SALogEvent/mildangFirebase';
import { messages as koMessages } from '../../../locales/ko';
import AppApolloProvider from '../../AppApolloProvider';
import GlobalLayout from '../../components/GlobalLayout';
// TODO(MR-23857): 학교 등록 팝업을 걷어낼 때 이 개발 도구도 함께 제거한다.
// import { SchoolCheckDevTools } from '@ui/components/student/pattern/SchoolInfoCheck';
import { ChatSidebarSyncRouteState } from './ChatSidebarSyncRouteState';
import AppSession from '@ui/components/user/patterns/InitStudentUtil/AppSession';
import useStudentWebNavChangeApp from 'hooks/useStudentWebNavChangeApp';
import { usePathname, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import NoSsr from 'components/NoSsr';

const InitStudentUtil = dynamic(
  () => import('@ui/components/user/patterns/InitStudentUtil/InitStudentUtil'),
  {
    ssr: false,
  },
);

const NetworkConnectionInfo = dynamic(() => import('@ui/components/recipes/NetworkConnectionInfo'), {
  ssr: false,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to handle navigation changes with proper Suspense wrapper
function NavChangeHandler() {
  useStudentWebNavChangeApp();
  return null;
}
function ActivityManagerInitializer() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isSSR) {
      const query: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        query[key] = value;
      });

      const path = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');

      const homeMatch = matchRoute(studentRoutes, path);

      activityManager.start({
        host: window.location.host,
        pathname: homeMatch?.template ?? '',
        path: path,
        query: {
          ...(homeMatch?.pathParams ?? {}),
          ...(homeMatch?.queryParams ?? {}),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default function ContextLayoutClient({ children }: { children: React.ReactNode }) {
  const { setHost } = useHostStore();

  useCheckClientVersion(pkg.version);

  useEffect(() => {
    if (!setHost) return;

    const initFirebase = async () => {
      await initMildangFirebaseApp();
    };

    setHost('STUDENT');
    initFirebase();
  }, [setHost]);

  const content = (
    <>
      <ChatSidebarSyncRouteState />
      <GlobalConfirm />
      <NewFeedListener />
      <Toaster />
      <GlobalLayout>{children}</GlobalLayout>
      {/* TODO(MR-23857): 학교 등록 팝업을 걷어낼 때 이 개발 도구도 함께 제거한다. */}
      {/* <SchoolCheckDevTools /> */}
      <InitStudentUtil disableNavigationEvents disableActivityMangerStart />
      <AppSession />
      <NetworkConnectionInfo />
      <NoSsr>
        <ChatSidebarSyncRouteState />
      </NoSsr>
    </>
  );

  return (
    <StrictMode>
      <LinguiClientProvider initialLocale="ko" initialMessages={koMessages}>
        <QueryClientProvider client={queryClient}>
          <IntlProvider timeZone={'Asia/Seoul'} locale="ko-KR" defaultLocale="ko-KR">
            <AppApolloProvider>
              <ThemeRegistry options={{ key: 'muicss' }}>
                <RouterSystemProvider routerType="app">
                  <Suspense fallback={<div>Loading...</div>}>
                    <ActivityManagerInitializer />
                    <NavChangeHandler />
                  </Suspense>
                  {content}
                </RouterSystemProvider>
              </ThemeRegistry>
            </AppApolloProvider>
          </IntlProvider>
        </QueryClientProvider>
      </LinguiClientProvider>
    </StrictMode>
  );
}
